/*
  File: flipbook-viewer.js
  - Self-contained IIFE, but delegating ESM import to `scripts.liquid`
  - Progressive enhancement: does nothing if no [data-flipbook-root].
*/

(function () {
  let isDevModeMemo = null;

  const normalTransitionDurationMs = 500;
  const reducedMotionTransitionDurationMs = 150;

  /**
   * @typedef {Object} StPageFlipObj - Represents the StPageFlip library constructor.
   * @property {Function} loadFromImages - Loads pages from an array of image URLs.
   * @property {Function} on - Registers a callback function for a specific event.
   * @property {Function} update - Updates the size and position of the viewer.
   * @property {Function} getCurrentPageIndex - Retrieves the current page index.
   * @property {Function} flipPrev - Flips to the previous page (with animation).
   * @property {Function} flipNext - Flips to the next page (with animation).
   * @property {Function} destroy - Destroys the viewer instance and event handlers.
   */

  /**
   * @typedef {Array<string>} Pages - An array of page URLs or identifiers to be displayed in the viewer.
   */

  /**
   * The StPageFlip-viewer instance, if available.
   * @type {StPageFlipObj|null} */
  let stPageFlip = null;

  /**
   * Represents a reference to the "PageFlip" library constructor.
   *
   * This variable attempts to assign the "PageFlip" library's constructor,
   * based on its availability in the global namespace.
   * - `PageFlip` when imported as an ESM module.
   * - `St.PageFlip` when imported via a script tag.
   * - `null` if the library is not available.
   * - See import method in `snippets/scripts.liquid`.
   * @type {Function | null} */
  const StPageFlipCtor = (window.PageFlip ? window.PageFlip : window.St && window.St.PageFlip) || null;

  /**
   * Check if the protocol is HTTP or the hostname is 'localhost' or '127.0.0.1'.
   * @return {boolean} - True if in development mode, false otherwise.
   */
  function isDevMode() {
    if (isDevModeMemo === null) {
      isDevModeMemo = location.protocol === 'http:' || location.hostname === 'localhost' || location.hostname === '127.0.0.1';
      if (isDevModeMemo) {
        devLog('info', 'Running in development mode.');
      }
    }
    return isDevModeMemo;
  }

  const devLogTag = '[flipbook-viewer]';

  /**
   * Log a message to the console if isDevMode()
   * @param {string} level - debug, log, info, warn, error
   * @param {string} message - the message to log
   * @param {any} data - optional additional data to log
   * @returns {void}
   */
  function devLog(level, message, data = undefined) {
    if (isDevMode()) {
      switch (level) {
        case 'debug':
          console.debug(`${devLogTag} ${message}`, data || '');
          break;
        case 'info':
          console.info(`${devLogTag} ${message}`, data || '');
          break;
        case 'warn':
          console.warn(`${devLogTag} ${message}`, data || '');
          break;
        case 'error':
          console.error(`${devLogTag} ${message}`, data || '');
          break;
        case 'log':
        default:
          console.log(`${devLogTag} ${message}`, data || '');
      }
    }
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else fn();
  }

  // Note: Previously we supported dynamic script loading via loadScript().
  // The project now attaches the PageFlip constructor globally in snippets/scripts.liquid.
  // We therefore remove the loader and rely on StPageFlipCtor above.

  // Compute size using container width and optional data-ratio (w:h)
  function getHeightToWidthRatio(element, attribute = 'data-ratio', defaultHeight = 4, defaultWidth = 3) {
    const defaultHeightToWidthRatio = defaultHeight / defaultWidth;
    let height = 0;
    let width = 0;
    const ratioAttr = element.getAttribute(attribute);
    if (ratioAttr && /^(\d+):(\d+)$/.test(ratioAttr)) {
      width = parseFloat(RegExp.$1);
      height = parseFloat(RegExp.$2);
    }
    return (height > 0 && width > 0) ? height / width : defaultHeightToWidthRatio;
  }

  function makeBlankPage(aspectWidth, aspectHeight, color = '#F3EADD', density = 'soft') {
    const svg = 'data:image/svg+xml;utf8,' + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${aspectWidth} ${aspectHeight}"><rect width="100%" height="100%" fill="${color}"/></svg>`
    );
    const page = document.createElement('div');
    page.className = 'page';
    page.setAttribute('data-density', density);
    page.innerHTML = `<div class="page-content"><div class="page-image" style="background-image:url('${svg}')" aria-hidden="true"></div></div>`;
    return page;
  }

  // Collect HTML page nodes for HTML mode (from template)
  function collectHtmlNodes(root) {
    const template = root.querySelector('[data-flipbook-pages]');
    if (!template) return [];
    let container;
    if (template.content) {
      container = template.content.cloneNode(true);
    } else {
      container = document.createElement('div');
      container.innerHTML = template.innerHTML;
    }

    const q = (sel) => container.querySelector(sel);
    const qa = (sel) => Array.prototype.slice.call(container.querySelectorAll(sel));

    const frontOutside = q('[data-role="front-outside"].page');
    const frontInside = q('[data-role="front-inside"].page');
    const inside = qa('[data-role="inside"].page');
    const backInside = q('[data-role="back-inside"].page');
    const backOutside = q('[data-role="back-outside"].page');

    const frontCoverPairCount = (frontOutside && frontInside) + (backInside && backOutside);
    const backCoverPairCount = (frontOutside && backInside) + (backInside && frontInside);
    const insidePagesCount = inside.length;

    const totalPagesCount = frontCoverPairCount + insidePagesCount + backCoverPairCount;
    const totalPagesIsEven = totalPagesCount % 2 === 0;
    const shouldPadSomewhere = totalPagesIsEven && (frontCoverPairCount + insidePagesCount) % 2 === 1;

    const frontCoverPairIsEven = frontCoverPairCount % 2 === 0;
    const backCoverPairIsEven = backCoverPairCount % 2 === 0;
    const insidePagesIsEven = insidePagesCount % 2 === 0;

    const shouldPadFrontCoverOutside = false;
    const shouldPadFrontCoverInside = false;

    const shouldPadInsidePagesStart = false;
    const shouldPadInsidePagesEnd = false;

    const shouldPadBackCoverInside = false;
    const shouldPadBackCoverOutside = false;

    const aspectHeight = getHeightToWidthRatio(root);
    const aspectWidth = 1;

    // Build blank page node using data-blank-color
    const color = root.getAttribute('data-blank-color') || '#F3EADD';

    const pages = [];

    // Front cover segment with blanks if needed
    pages.push(shouldPadFrontCoverOutside ? makeBlankPage(aspectWidth, aspectHeight, color, 'hard') : frontOutside);
    pages.push(shouldPadFrontCoverInside ? makeBlankPage(aspectWidth, aspectHeight, color, 'hard') : frontInside);

    // Inside pages
    if (shouldPadInsidePagesStart) pages.push(makeBlankPage(aspectWidth, aspectHeight, color, 'soft'));
    pages.push.apply(pages, inside);
    if (shouldPadInsidePagesEnd) pages.push(makeBlankPage(aspectWidth, aspectHeight, color, 'soft'));

    // Back cover segment with blanks if needed
    pages.push(shouldPadBackCoverInside ? makeBlankPage(aspectWidth, aspectHeight, color, 'hard') : backInside);
    pages.push(shouldPadBackCoverOutside ? makeBlankPage(aspectWidth, aspectHeight, color, 'hard') : backOutside);

    devLog('debug', `Collected ${pages.length} HTML pages (role-aware).`);
    return pages;
  }

  // Collect image URLs for images-mode fallback
  function collectPages(root) {
    const template = root.querySelector('[data-flipbook-pages]');
    if (!template) return [];
    let container;
    if (template.content) {
      container = template.content.cloneNode(true);
    } else {
      // Fallback for older browsers: parse innerHTML into a div
      container = document.createElement('div');
      container.innerHTML = template.innerHTML;
    }

    const pick = (sel) => {
      const el = container.querySelector(sel);
      return el ? el.getAttribute('data-page-src') : null;
    };
    const pickAll = (sel) => [...container.querySelectorAll(sel)].map((el) => el.getAttribute('data-page-src')).filter(Boolean);

    // Cover pieces
    const frontOutside = pick('[data-role="front-outside"]');
    const frontInside = pick('[data-role="front-inside"]');
    const insidePages = pickAll('[data-role="inside"]');
    const backInside = pick('[data-role="back-inside"]');
    const backOutside = pick('[data-role="back-outside"]');

    // TODO - svg width/height to match pages ratio
    // Lightweight blank page (SVG) — 3:4 viewBox to roughly match default ratio.
    const blankSvg = (
      'data:image/svg+xml;utf8,' +
      encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 3 4"><rect width="100%" height="100%" fill="#F3EADD"/></svg>')
    );

    const pages = [];
    // Front cover segment
    if (frontOutside || frontInside) {
      if (frontOutside && frontInside) {
        pages.push(frontOutside, frontInside);
      } else if (frontOutside && !frontInside) {
        pages.push(frontOutside, blankSvg);
      } else if (!frontOutside && frontInside) {
        pages.push(blankSvg, frontInside);
      }
    }
    // Inside pages
    pages.push(...insidePages);

    // TODO - we should check that inside-pages are of even amount
    //  but not yet sure when to back/front pad?

    // Back cover segment
    if (backInside || backOutside) {
      if (backInside && backOutside) {
        pages.push(backInside, backOutside);
      } else if (backInside && !backOutside) {
        pages.push(backInside, blankSvg);
      } else if (!backInside && backOutside) {
        pages.push(blankSvg, backOutside);
      }
    }

    devLog('debug', `Collected ${pages.length} pages (role-aware) from template.`, pages);
    return pages;
  }

  function initInlineViewer(root) {
    const stage = root.querySelector('.flipbook-stage');
    const controls = root.querySelector('.flipbook-controls');
    const navs = root.querySelector('.flipbook-navs');
    const openBtn = root.querySelector('[data-action="open"]');
    const htmlNodes = collectHtmlNodes(root);
    const hasHtmlPages = htmlNodes && htmlNodes.length > 0;
    const pages = collectPages(root); // images fallback
    const state = {index: 0, pages: pages};
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let resizeObserver = null;
    const autoOpen = String(root.getAttribute('data-auto-open')) === 'true';
    const hideOpenClose = String(root.getAttribute('data-hide-open-close')) === 'true';

    if (!hasHtmlPages && pages.length === 0) {
      devLog('info', 'No pages found in template.');
      return;
    }


    function setStatus(currentIndex) {
      const status = root.querySelector('.flipbook-status');
      if (status) {
        let total = state.pages.length;
        try { if (stPageFlip && typeof stPageFlip.getPageCount === 'function') total = stPageFlip.getPageCount(); } catch(_){}
        status.textContent = (currentIndex + 1) + ' / ' + total;
      }
    }

    function renderInline() {
      if (stage) {
        stage.innerHTML = '';
        const img = document.createElement('img');
        img.src = state.pages[state.index];
        img.alt = 'Page ' + (state.index + 1) + ' of ' + state.pages.length;
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        stage.appendChild(img);
        setStatus(state.index);
      } else {
        devLog('warn', 'Could not renderInline(), no stage element found.');
      }
    }

    function setUI(opened) {
      if (stage) stage.hidden = !opened;
      if (controls) controls.hidden = !opened;
      if (navs) navs.hidden = !opened;
      if (openBtn && !hideOpenClose) openBtn.hidden = opened;
      try { if (openBtn && !hideOpenClose) openBtn.setAttribute('aria-expanded', opened ? 'true' : 'false'); } catch(_) {}
    }



    function ensureInitialized() {
      if (stPageFlip || !StPageFlipCtor || !stage) return;
      // Compute size using container width and optional data-ratio (w:h)
      let heighToWidthRatio = getHeightToWidthRatio(root);

      const containerWidth = stage.clientWidth || root.clientWidth || 600;
      const containerHeight = Math.round(containerWidth * heighToWidthRatio);
      try { stage.style.minHeight = containerHeight + 'px'; } catch (_) {}
      try {
        stPageFlip = new StPageFlipCtor(stage, {
          width: containerWidth,
          height: containerHeight,
          size: 'stretch',
          minWidth: 320,
          maxWidth: 1920,
          minHeight: 320,
          maxHeight: 1920,
          maxShadowOpacity: 0.5,
          // Force cover behavior so we start on a single outside cover page
          showCover: true,
          useMouseEvents: true,
          mobileScrollSupport: true,
          clickEventForward: true,
          disableFlipByClick: false,
          flippingTime: (reduceMotion)
              ? reducedMotionTransitionDurationMs
              : normalTransitionDurationMs,
          startIndex: 0
        });

        // Prefer HTML mode if available
        if (hasHtmlPages && typeof stPageFlip.loadFromHTML === 'function') {
          // Create a container and attach HTML pages to DOM for the library
          const bookEl = document.createElement('div');
          bookEl.className = 'flip-book';
          try { stage.appendChild(bookEl); } catch(_){ }
          htmlNodes.forEach(function(node){ try { bookEl.appendChild(node); } catch(_){} });
          const pageNodes = bookEl.querySelectorAll('.page');
          stPageFlip.loadFromHTML(pageNodes);
        } else {
          stPageFlip.loadFromImages(state.pages);
        }
        if (typeof stPageFlip.on === 'function') {
          stPageFlip.on('flip', function (e) {
            setStatus(e.data);
            try { root.dispatchEvent(new CustomEvent('flipbook:flip', { detail: { index: e && e.data }, bubbles: true })); } catch(_) {}
          });
        }
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(function () {
            const updatedWidth = stage.clientWidth || root.clientWidth || containerWidth;
            const updatedHeight = Math.round(updatedWidth * heighToWidthRatio);
            try { stPageFlip.update({width: updatedWidth, height: updatedHeight}); } catch (e) { devLog('error', 'Failed to update StPageFlip stage size.', e); }
          });
          resizeObserver.observe(stage);
        }
      } catch (e) {
        devLog('error', 'Failed to initialize StPageFlip lib.', e);
      }
    }

    function open() {
      setUI(true);
      if (!stPageFlip) ensureInitialized();
      if (stPageFlip && typeof stPageFlip.getCurrentPageIndex === 'function') {
        setStatus(stPageFlip.getCurrentPageIndex());
      } else {
        renderInline();
      }
      // Move focus to controls for keyboard users
      const nextBtn = root.querySelector('[data-action="next"]');
      if (nextBtn) {
        try { nextBtn.focus(); } catch (_) {}
      }
    }

    function close() {
      setUI(false);
      if (openBtn && !hideOpenClose) {
        try { openBtn.focus(); } catch (_) {}
      }
      // Note: keep instance alive; no destroy() here. Hard teardown on pagehide/beforeunload only.
    }

    function prev() {
      if (stPageFlip) {
        try {
          if (reduceMotion && typeof stPageFlip.turnToPrevPage === 'function') {
            stPageFlip.turnToPrevPage();
          } else if (typeof stPageFlip.flipPrev === 'function') {
            stPageFlip.flipPrev('bottom');
          } else if (typeof stPageFlip.turnToPrevPage === 'function') {
            stPageFlip.turnToPrevPage();
          }
        } catch (_) {}
      } else {
        state.index = (state.index - 1 + state.pages.length) % state.pages.length;
        renderInline();
      }
    }

    function next() {
      if (stPageFlip) {
        try {
          if (reduceMotion && typeof stPageFlip.turnToNextPage === 'function') {
            stPageFlip.turnToNextPage();
          } else if (typeof stPageFlip.flipNext === 'function') {
            stPageFlip.flipNext('bottom');
          } else if (typeof stPageFlip.turnToNextPage === 'function') {
            stPageFlip.turnToNextPage();
          }
        } catch (_) {}
      } else {
        state.index = (state.index + 1) % state.pages.length;
        renderInline();
      }
    }

    function goTo(index) {
      index = Math.max(0, parseInt(index || 0, 10) || 0);
      if (stPageFlip) {
        try {
          if (typeof stPageFlip.flip === 'function') {
            // Some versions expose flip(index)
            stPageFlip.flip(index);
            return;
          }
          if (typeof stPageFlip.turnToPage === 'function') {
            stPageFlip.turnToPage(index);
            return;
          }
          if (typeof stPageFlip.getCurrentPageIndex === 'function') {
            var current = stPageFlip.getCurrentPageIndex();
            var steps = Math.min(20, Math.abs(index - current));
            if (index > current) {
              for (var i = 0; i < steps; i++) {
                if (typeof stPageFlip.turnToNextPage === 'function') stPageFlip.turnToNextPage();
                else if (typeof stPageFlip.flipNext === 'function') stPageFlip.flipNext('bottom');
              }
            } else if (index < current) {
              for (var j = 0; j < steps; j++) {
                if (typeof stPageFlip.turnToPrevPage === 'function') stPageFlip.turnToPrevPage();
                else if (typeof stPageFlip.flipPrev === 'function') stPageFlip.flipPrev('bottom');
              }
            }
            return;
          }
        } catch (_) {}
      }
      // Fallback (no PageFlip): update inline state
      state.index = Math.max(0, Math.min(index, state.pages.length - 1));
      renderInline();
      try { root.dispatchEvent(new CustomEvent('flipbook:flip', { detail: { index: state.index }, bubbles: true })); } catch(_) {}
    }

    // Wire controls
    root.addEventListener('click', function (e) {
      const btn = e.target && e.target.closest('[data-action]');
      const action = btn && btn.getAttribute('data-action');
      if (action) {
        if (action === 'open') open();
        else if (action === 'close') close();
        else if (action === 'prev') prev();
        else if (action === 'next') next();
      } else {
        // Ignore clicks outside our own control elements.
      }
    });

    // External navigation: go to specific page index (0-based)
    root.addEventListener('flipbook:goTo', function (e) {
      if (!e || !e.detail) return;
      const index = parseInt(e.detail.index) || 0;
      if (!stPageFlip) {
        ensureInitialized();
        if (autoOpen) {
          // open() will call ensureInitialized again but keeps UI/state consistent
          open();
        }
      }
      goTo(index);
    });

    // Initialize StPageFlip v2.0.0 when near the viewport (no dynamic loading)
    const io = new IntersectionObserver(function (entries) {
      if (entries.some((entry) => entry.isIntersecting)) {
        io.disconnect();
        devLog('debug', 'Intersection target reached. Initializing StPageFlip if available...');

        try {
          if (StPageFlipCtor) {
            ensureInitialized();
            if (autoOpen) open();
          } else {
            devLog('warn', 'StPageFlip constructor not found; keeping inline pager fallback.');
          }
        } catch (e) {
          devLog('error', 'Failed to initialize StPageFlip lib.', e);
        }
      }
    }, {rootMargin: '200px'});
    io.observe(root);

    // Keyboard controls when open
    root.addEventListener('keydown', function (e) {
      // Only handle when stage/controls are visible
      const isOpen = stage && !stage.hidden;
      if (!isOpen) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'Escape') { e.preventDefault(); close(); }
    });

    // Teardown on page hide/unload
    window.addEventListener('pagehide', function () { try { close(); } catch (_) {} });
    window.addEventListener('beforeunload', function () { try { close(); } catch (_) {} });
  }

  ready(function () {
    const root = document.querySelector('[data-flipbook-root]');
    if (root) {
      devLog('info', 'Initializing inline viewer.');
      initInlineViewer(root);
    } else {
      devLog('warn', 'No [data-flipbook-root] element found.');
    }
  });
})();
